import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Button } from "components/core/Button";
import { Popup, FilterSection, FilterActionButtons } from "components/molecules";

import "./FilterPanel.scss";

const FilterPanel = ({
	isOpen,
	onToggle,
	title,
	subtitle,
	sections,
	onClearAll,
	onSelectAll,
	onApply,
	applyText,
	label,
	icon: Icon,
	selectedCount,
	className,
	showHeader
}) => {
	const hasAnySelections = sections.some((section) => section.selectedOptions.length > 0);
	const allSelected = sections.every((section) => {
		const optionsCount = Array.isArray(section.options)
			? section.options.length
			: Object.keys(section.options).length;
		return section.selectedOptions.length === optionsCount;
	});

	const triggerButton = (
		<Button theme="basic" className="filter-panel-trigger">
			{Icon && <Icon className="filter-panel-icon" />}
			{label}
			{selectedCount > 0 && (
				<span className="filter-badge">{selectedCount}</span>
			)}
		</Button>
	);

	const panelContent = (
		<div className="filter-panel-content">
			{/* Header */}
			{showHeader && (
				<div className="filter-panel-header">
					{title && <h2 className="filter-panel-title">{title}</h2>}
					{subtitle && <span className="filter-panel-subtitle">{subtitle}</span>}
				</div>
			)}

			{/* Filters Header */}
			<div className="filters-header">
				<span className="filters-label">Filters</span>
				<FilterActionButtons
					onClearAll={onClearAll}
					onSelectAll={onSelectAll}
					hasSelections={hasAnySelections}
					allSelected={allSelected}
				/>
			</div>

			<div className="filters-divider" />

			{/* Filter Sections */}
			{sections.map((section, index) => (
				<React.Fragment key={section.id || section.title}>
					<FilterSection
						title={section.title}
						options={section.options}
						selectedOptions={section.selectedOptions}
						onToggleOption={section.onToggleOption}
						footer={section.footer}
					/>
					{index < sections.length - 1 && <div className="filters-divider" />}
				</React.Fragment>
			))}

			{/* Apply Button */}
			{onApply && (
				<Button
					theme="primary"
					className="apply-filters-btn"
					onClick={onApply}
				>
					{applyText}
				</Button>
			)}
		</div>
	);

	return (
		<Popup
			buttonContent={triggerButton}
			position="bottom-right"
			open={isOpen}
			onToggle={onToggle}
			className={classnames("filter-panel", className)}
		>
			{panelContent}
		</Popup>
	);
};

FilterPanel.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
	title: PropTypes.string,
	subtitle: PropTypes.string,
	sections: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			title: PropTypes.string.isRequired,
			options: PropTypes.oneOfType([
				PropTypes.arrayOf(
					PropTypes.oneOfType([
						PropTypes.string,
						PropTypes.shape({
							value: PropTypes.string.isRequired,
							label: PropTypes.string.isRequired
						})
					])
				),
				PropTypes.objectOf(
					PropTypes.shape({
						value: PropTypes.string.isRequired,
						label: PropTypes.string.isRequired
					})
				)
			]).isRequired,
			selectedOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
			onToggleOption: PropTypes.func.isRequired,
			footer: PropTypes.node
		})
	).isRequired,
	onClearAll: PropTypes.func.isRequired,
	onSelectAll: PropTypes.func.isRequired,
	onApply: PropTypes.func,
	applyText: PropTypes.string,
	label: PropTypes.string,
	icon: PropTypes.elementType,
	selectedCount: PropTypes.number,
	className: PropTypes.string,
	showHeader: PropTypes.bool
};

FilterPanel.defaultProps = {
	title: "",
	subtitle: "",
	onApply: null,
	applyText: "Apply Filters",
	label: "Filters",
	icon: null,
	selectedCount: 0,
	className: "",
	showHeader: true
};

export default FilterPanel;
